import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { FlotiqLogo, WarningTriangleRedIcon } from '../../images/shapes';

import Button from '../Button/Button';
import Heading from '../Heading/Heading';

interface ErrorFallbackProps {
  /**
   * Error details
   */
  error?: unknown;
  /**
   * Component Stack
   */
  componentStack?: string;
  /**
   * Event id
   */
  eventId?: string;
  /**
   * Reset error
   */
  resetError?: () => void;
  /**
   *  Support button click handler
   */
  supportButtonClick?: (message: string) => Promise<void>;
  /**
   * Test id for Error Fallback Component
   */
  testId?: string;
}

const ErrorFallback = ({
  error,
  componentStack = '',
  resetError,
  supportButtonClick = undefined,
  testId = '',
}: ErrorFallbackProps) => {
  const { t } = useTranslation();
  const [issueSend, setIssueSend] = useState<boolean>(false);

  const handleReset = useCallback(() => {
    resetError?.();
  }, [resetError]);

  const message = useMemo((): string => {
    if (typeof error === 'string') {
      return error;
    }

    if (typeof error === 'object' && error && 'message' in error) {
      return error.message as string;
    }

    return JSON.stringify(error);
  }, [error]);

  const handlerSupportButtonClick = async (message: string) => {
    setIssueSend(true);
    if (supportButtonClick) {
      supportButtonClick(message);
    }
  };

  return (
    <div
      className={twMerge(
        'bg-white dark:bg-slate-900 p-4 w-full',
        'flex flex-col justify-center items-center',
      )}
      {...getTestProps(testId, 'container')}
    >
      <Button
        onClick={handleReset}
        buttonColor="borderless"
        {...getTestProps(testId, 'logo', 'testId')}
      >
        <FlotiqLogo className="h-10 mb-4" />
      </Button>

      <Heading
        level={4}
        alignment={'center'}
        additionalClasses="mb-4 xl:text-4xl dark:text-slate-200"
        {...getTestProps(testId, 'title', 'testId')}
      >
        <div className="flex flex-row justify-center items-center">
          <WarningTriangleRedIcon className="w-6 h-6 mr-4" />
          {t('ErrorBoundary.SomethingWentWrong')}
        </div>
      </Heading>

      <details
        className={twMerge(
          'px-4 py-2 whitespace-pre-wrap mb-4 dark:text-slate-200',
          'max-h-[60vh] overflow-auto border border-slate-100 dark:border-slate-800 rounded-lg scrollbar-sm',
        )}
        open
        {...getTestProps(testId, 'details')}
      >
        {message}
        <br />
        {componentStack}
      </details>

      <div className="flex flex-row justify-center items-center mt-4">
        {supportButtonClick && (
          <Button
            type="button"
            buttonSize="lg"
            additionalClasses="mr-2"
            disabled={issueSend}
            onClick={() => handlerSupportButtonClick(message)}
            {...getTestProps(testId, 'support-button', 'testId')}
          >
            {t('ErrorBoundary.ReportFeedback')}
          </Button>
        )}

        <Button
          type="button"
          buttonSize="lg"
          onClick={handleReset}
          {...getTestProps(testId, 'reset', 'testId')}
        >
          {t('ErrorBoundary.ClickHereToReset')}
        </Button>
      </div>
    </div>
  );
};

export default ErrorFallback;
